/**
 * Icon registry — the DS icon vocabulary, FontAwesome 5 name → Lucide component.
 *
 * Brand core V13 (chap. 5) puts the app on Lucide, trait 1,5 px, « remplacement écran par
 * écran ». So the *keys* stay the FontAwesome names the DS already calls (`<Icon name="home">`
 * keeps working everywhere) while the *values* are the Lucide glyphs actually drawn.
 *
 * `IconName` is `keyof` this map, not the whole FA5 glyphmap: the vocabulary is now an explicit,
 * reviewable list, and `ds:tsc` fails on a name nobody has mapped instead of shipping a blank box.
 * Adding an icon = adding a line here.
 *
 * Icons are imported one subpath at a time (`lucide-react-native/icons/<kebab>`) rather than from
 * the package barrel: Metro does not tree-shake, and the barrel is 3 700 glyphs.
 *
 * ⚠️ Un alias Lucide a bien un `.d.ts` mais pas de `.mjs` : `icons/trash-2` typecheck et ne se
 * résout pas (c'est `icons/trash` qui porte le dessin). `ds:tsc` ne voit donc rien ; ce qui
 * l'attrape, c'est la story `Atoms/Icon → Gallery`, qui rend le registre en entier. Après un ajout
 * ici, ouvrez-la.
 *
 * Correspondances où Lucide ne dit pas tout à fait la même chose que FontAwesome :
 *  - `file-pdf` → `FileText` : Lucide n'a pas d'icône PDF, seulement un document générique ;
 *  - `telegram-plane` / `whatsapp` → `Send` / `MessageCircle` : Lucide ne dessine aucune marque,
 *    c'est le geste qui est représenté, plus le service ;
 *  - `magic` → `Sparkles` : le registre V13 est « ce que l'IA vient de faire », pas la baguette ;
 *  - `bolt` → `Zap`, `signal` → `Signal` : même objet, autre nom.
 */
import Archive from "lucide-react-native/icons/archive"
import Compass from "lucide-react-native/icons/compass"
import Eraser from "lucide-react-native/icons/eraser"
import Ghost from "lucide-react-native/icons/ghost"
import CornerDownLeft from "lucide-react-native/icons/corner-down-left"
import Paperclip from "lucide-react-native/icons/paperclip"
import PanelLeftClose from "lucide-react-native/icons/panel-left-close"
import PanelLeftOpen from "lucide-react-native/icons/panel-left-open"
import PanelRightClose from "lucide-react-native/icons/panel-right-close"
import PanelRightOpen from "lucide-react-native/icons/panel-right-open"
import ArrowLeftRight from "lucide-react-native/icons/arrow-left-right"
import ArrowRight from "lucide-react-native/icons/arrow-right"
import Award from "lucide-react-native/icons/award"
import Bell from "lucide-react-native/icons/bell"
import Book from "lucide-react-native/icons/book"
import BookOpen from "lucide-react-native/icons/book-open"
import Baby from "lucide-react-native/icons/baby"
import Calculator from "lucide-react-native/icons/calculator"
import Calendar from "lucide-react-native/icons/calendar"
import CalendarCheck from "lucide-react-native/icons/calendar-check"
import CalendarClock from "lucide-react-native/icons/calendar-clock"
import CalendarDays from "lucide-react-native/icons/calendar-days"
import CalendarX from "lucide-react-native/icons/calendar-x"
import CalendarPlus from "lucide-react-native/icons/calendar-plus"
import Camera from "lucide-react-native/icons/camera"
import ChartLine from "lucide-react-native/icons/chart-line"
import Check from "lucide-react-native/icons/check"
import ChevronDown from "lucide-react-native/icons/chevron-down"
import ChevronLeft from "lucide-react-native/icons/chevron-left"
import ChevronRight from "lucide-react-native/icons/chevron-right"
import ChevronUp from "lucide-react-native/icons/chevron-up"
import CircleAlert from "lucide-react-native/icons/circle-alert"
import CircleCheck from "lucide-react-native/icons/circle-check"
import CircleMinus from "lucide-react-native/icons/circle-minus"
import CirclePause from "lucide-react-native/icons/circle-pause"
import CirclePlus from "lucide-react-native/icons/circle-plus"
import CircleQuestionMark from "lucide-react-native/icons/circle-question-mark"
import CircleX from "lucide-react-native/icons/circle-x"
import Clock from "lucide-react-native/icons/clock"
import CloudUpload from "lucide-react-native/icons/cloud-upload"
import Columns2 from "lucide-react-native/icons/columns-2"
import Copy from "lucide-react-native/icons/copy"
import CreditCard from "lucide-react-native/icons/credit-card"
import Download from "lucide-react-native/icons/download"
import Dumbbell from "lucide-react-native/icons/dumbbell"
import ExternalLink from "lucide-react-native/icons/external-link"
import Eye from "lucide-react-native/icons/eye"
import EyeOff from "lucide-react-native/icons/eye-off"
import File from "lucide-react-native/icons/file"
import FileImage from "lucide-react-native/icons/file-image"
import FileText from "lucide-react-native/icons/file-text"
import Flag from "lucide-react-native/icons/flag"
import Flame from "lucide-react-native/icons/flame"
import Flower from "lucide-react-native/icons/flower"
import Gift from "lucide-react-native/icons/gift"
import Globe from "lucide-react-native/icons/globe"
import GraduationCap from "lucide-react-native/icons/graduation-cap"
import HandCoins from "lucide-react-native/icons/hand-coins"
import Handshake from "lucide-react-native/icons/handshake"
import Headset from "lucide-react-native/icons/headset"
import Heart from "lucide-react-native/icons/heart"
import Hourglass from "lucide-react-native/icons/hourglass"
import House from "lucide-react-native/icons/house"
import Images from "lucide-react-native/icons/images"
import Info from "lucide-react-native/icons/info"
import LayoutDashboard from "lucide-react-native/icons/layout-dashboard"
import LayoutGrid from "lucide-react-native/icons/layout-grid"
import Lightbulb from "lucide-react-native/icons/lightbulb"
import Link from "lucide-react-native/icons/link"
import ListChecks from "lucide-react-native/icons/list-checks"
import Lock from "lucide-react-native/icons/lock"
import Mail from "lucide-react-native/icons/mail"
import Maximize2 from "lucide-react-native/icons/maximize-2"
import MessageCircle from "lucide-react-native/icons/message-circle"
import Minimize2 from "lucide-react-native/icons/minimize-2"
import Mic from "lucide-react-native/icons/mic"
import Pause from "lucide-react-native/icons/pause"
import Pencil from "lucide-react-native/icons/pencil"
import Play from "lucide-react-native/icons/play"
import Plus from "lucide-react-native/icons/plus"
import Presentation from "lucide-react-native/icons/presentation"
import ReceiptEuro from "lucide-react-native/icons/receipt-euro"
import ReceiptText from "lucide-react-native/icons/receipt-text"
import RefreshCw from "lucide-react-native/icons/refresh-cw"
import RotateCw from "lucide-react-native/icons/rotate-cw"
import School from "lucide-react-native/icons/school"
import Search from "lucide-react-native/icons/search"
import Send from "lucide-react-native/icons/send"
import Settings from "lucide-react-native/icons/settings"
import Share2 from "lucide-react-native/icons/share-2"
import Signal from "lucide-react-native/icons/signal"
import SlidersHorizontal from "lucide-react-native/icons/sliders-horizontal"
import Sparkles from "lucide-react-native/icons/sparkles"
import Square from "lucide-react-native/icons/square"
import SquareCheck from "lucide-react-native/icons/square-check"
import SquarePen from "lucide-react-native/icons/square-pen"
import Star from "lucide-react-native/icons/star"
import Trash from "lucide-react-native/icons/trash"
import Timer from "lucide-react-native/icons/timer"
import TriangleAlert from "lucide-react-native/icons/triangle-alert"
import Undo2 from "lucide-react-native/icons/undo-2"
import User from "lucide-react-native/icons/user"
import Users from "lucide-react-native/icons/users"
import X from "lucide-react-native/icons/x"
import Zap from "lucide-react-native/icons/zap"
import ZoomIn from "lucide-react-native/icons/zoom-in"
import ZoomOut from "lucide-react-native/icons/zoom-out"
import Euro from "lucide-react-native/icons/euro"
import UserPlus from "lucide-react-native/icons/user-plus"
import CheckCheck from "lucide-react-native/icons/check-check"
// L'entrée dans le live de l'élève : le test du matériel et ses conseils, les réponses à Sam.
import Cable from "lucide-react-native/icons/cable"
import DoorOpen from "lucide-react-native/icons/door-open"
import Hand from "lucide-react-native/icons/hand"
import Headphones from "lucide-react-native/icons/headphones"
import Keyboard from "lucide-react-native/icons/keyboard"
import LockOpen from "lucide-react-native/icons/lock-open"
import MoveHorizontal from "lucide-react-native/icons/move-horizontal"
import Smartphone from "lucide-react-native/icons/smartphone"
import SquareX from "lucide-react-native/icons/square-x"
import Video from "lucide-react-native/icons/video"
import VideoOff from "lucide-react-native/icons/video-off"
import Volume2 from "lucide-react-native/icons/volume-2"
import VolumeX from "lucide-react-native/icons/volume-x"
import Wifi from "lucide-react-native/icons/wifi"

// Ajouts du parcours « Activer la suite » — la répartition des heures et la formule solo.
import Minus from "lucide-react-native/icons/minus"
import Scale from "lucide-react-native/icons/scale"
// « Se déconnecter » — le pied de la feuille de profil de l'espace parent.
import LogOut from "lucide-react-native/icons/log-out"
// La poignée d'une ligne qu'on déplace (réordonner ses activités, accueil élève).
import GripVertical from "lucide-react-native/icons/grip-vertical"
// Le jeu de l'élève : les deux gestes de la boutique, la coupure d'un classement.
import Sprout from "lucide-react-native/icons/sprout"
import Utensils from "lucide-react-native/icons/utensils"
import Ellipsis from "lucide-react-native/icons/ellipsis"
// L'écran d'activité de l'élève : « Revoir le quiz », et l'avis sur une correction.
import ClipboardCheck from "lucide-react-native/icons/clipboard-check"
import ThumbsDown from "lucide-react-native/icons/thumbs-down"
import ThumbsUp from "lucide-react-native/icons/thumbs-up"
// Le live de l'élève : la barre de contrôle, les vignettes, « Un problème ? ».
import MicOff from "lucide-react-native/icons/mic-off"
import Monitor from "lucide-react-native/icons/monitor"
import ScreenShareOff from "lucide-react-native/icons/screen-share-off"
import PhoneOff from "lucide-react-native/icons/phone-off"
import Stethoscope from "lucide-react-native/icons/stethoscope"
import SwitchCamera from "lucide-react-native/icons/switch-camera"
// La fiche « Ma lecture » de l'énoncé : « Aa » qui l'ouvre ; et la flèche de retour (R53, TRANSITION.md).
import ALargeSmall from "lucide-react-native/icons/a-large-small"
import ArrowLeft from "lucide-react-native/icons/arrow-left"
// Le guidage de la question du navigateur : la flèche vers la question.
import ArrowUp from "lucide-react-native/icons/arrow-up"

import { type LucideIcon } from "lucide-react-native"

export const ICONS = {
    /** Archiver une activité — le carton du livre, pas la corbeille : rien n'est supprimé. */
    archive: Archive,
    "arrow-right": ArrowRight,
    bell: Bell,
    bolt: Zap,
    book: Book,
    /** Onglet « Activités » de l'espace parent — le livre ouvert de la maquette. */
    "book-open": BookOpen,
    calculator: Calculator,
    calendar: Calendar,
    "calendar-alt": CalendarDays,
    "calendar-times": CalendarX,
    /** Décaler une séance — Lucide dessine l'horloge sur le calendrier, FontAwesome 5 non. */
    "calendar-clock": CalendarClock,
    "calendar-plus": CalendarPlus,
    camera: Camera,
    /** Retourner ma caméra en live (le nom de FontAwesome 6, comme l'ancien bouton). */
    "camera-rotate": SwitchCamera,
    "chalkboard-teacher": Presentation,
    "chart-line": ChartLine,
    check: Check,
    "check-circle": CircleCheck,
    "check-square": SquareCheck,
    "chevron-down": ChevronDown,
    "chevron-left": ChevronLeft,
    "chevron-right": ChevronRight,
    "chevron-up": ChevronUp,
    clock: Clock,
    "cloud-upload-alt": CloudUpload,
    cog: Settings,
    "comment-dots": MessageCircle,
    /** Le partage d'écran du masteur revenu à sa place, à côté de l'énoncé. */
    "compress-alt": Minimize2,
    /** Écrire à un tuteur — la bulle, quand `whatsapp` dirait le canal plutôt que le geste. */
    comment: MessageCircle,
    copy: Copy,
    "credit-card": CreditCard,
    download: Download,
    /** « S'entraîner » — le parcours d'exercices similaires. */
    dumbbell: Dumbbell,
    edit: SquarePen,
    envelope: Mail,
    "exchange-alt": ArrowLeftRight,
    "exclamation-circle": CircleAlert,
    "exclamation-triangle": TriangleAlert,
    /** Le partage d'écran du masteur en grand, l'énoncé masqué (comme l'ancien live). */
    "expand-alt": Maximize2,
    "external-link-alt": ExternalLink,
    eye: Eye,
    "eye-slash": EyeOff,
    file: File,
    "file-image": FileImage,
    "file-invoice": ReceiptText,
    "file-invoice-dollar": ReceiptEuro,
    "file-pdf": FileText,
    /** Onglet « Rapports » — un document, pas une courbe de statistiques. */
    "file-lines": FileText,
    fire: Flame,
    flag: Flag,
    gift: Gift,
    /** « Ressemble à du 3ème » — la recherche du chapitre dans les autres classes. */
    globe: Globe,
    "graduation-cap": GraduationCap,
    "hand-holding-usd": HandCoins,
    handshake: Handshake,
    headset: Headset,
    heart: Heart,
    home: House,
    /** Onglet « Accueil » de l'espace parent — le tableau de bord de la maquette. */
    "layout-dashboard": LayoutDashboard,
    "hourglass-half": Hourglass,
    /** « Indice » — la suggestion rapide d'une conversation avec Sam. */
    lightbulb: Lightbulb,
    "info-circle": Info,
    link: Link,
    lock: Lock,
    magic: Sparkles,
    /** « Message vocal » — la source d'un composeur de conversation. */
    microphone: Mic,
    pause: Pause,
    "pause-circle": CirclePause,
    /** Écouter un message vocal ; `pause` existait déjà seul. */
    play: Play,
    "pencil-alt": Pencil,
    "photo-video": Images,
    plus: Plus,
    "plus-circle": CirclePlus,
    /** L'aide — le bouton du header qui ouvre la messagerie du support (`<HelpBubble>`). */
    "question-circle": CircleQuestionMark,
    paperclip: Paperclip,
    "level-down-alt": CornerDownLeft,
    redo: RotateCw,
    school: School,
    search: Search,
    "search-minus": ZoomOut,
    "search-plus": ZoomIn,
    "share-alt": Share2,
    signal: Signal,
    // Le test du matériel à l'entrée du live, ses conseils et ses gestes (les glyphes d'aujourd'hui).
    wifi: Wifi,
    "volume-up": Volume2,
    "volume-mute": VolumeX,
    video: Video,
    "video-slash": VideoOff,
    headphones: Headphones,
    keyboard: Keyboard,
    unlock: LockOpen,
    "hand-paper": Hand,
    "mobile-alt": Smartphone,
    ghost: Ghost,
    compass: Compass,
    eraser: Eraser,
    "panel-left-close": PanelLeftClose,
    "panel-left-open": PanelLeftOpen,
    "panel-right-close": PanelRightClose,
    "panel-right-open": PanelRightOpen,
    "door-open": DoorOpen,
    "arrows-alt-h": MoveHorizontal,
    ethernet: Cable,
    /** « Ferme l'autre appli » : la fenêtre barrée, le conseil d'aujourd'hui. */
    "window-close": SquareX,
    "sliders-h": SlidersHorizontal,
    square: Square,
    star: Star,
    /** Le chrono d'une activité — « 12 / 20 min ». Lucide n'a pas de chronomètre, il a un minuteur. */
    stopwatch: Timer,
    "sync-alt": RefreshCw,
    tasks: ListChecks,
    "telegram-plane": Send,
    "th-large": LayoutGrid,
    columns: Columns2,
    times: X,
    "times-circle": CircleX,
    trash: Trash,
    "undo-alt": Undo2,
    "user-friends": Users,
    "user-graduate": GraduationCap,
    /** Cours individuel — une personne, quand `users` dit le groupe. */
    user: User,
    users: Users,
    whatsapp: MessageCircle,
    // Tableau de parrainage : les trois étapes « Comment ça marche » du legacy.
    "user-plus": UserPlus,
    "euro-sign": Euro,

    // Parcours « Résilier l'abonnement » — l'enfant qui décroche, la prise de RDV, la baisse d'heures.
    child: Baby,
    "calendar-check": CalendarCheck,
    "minus-circle": CircleMinus,
    "check-double": CheckCheck,
    /** « Heures par matière » du composeur d'activation — la balance du legacy. */
    "balance-scale": Scale,
    /** « Cours particuliers » : un élève seul, là où `users` en dessine deux. */
    "user-alt": User,
    /** Le « − » des pas à pas de la répartition des heures ; `plus` existait déjà seul. */
    minus: Minus,
    /** « Se déconnecter » : la porte de sortie, quand `exchange-alt` disait « changer ». */
    "sign-out-alt": LogOut,
    // Les compteurs de l'élève : les pétales et le titre gagné (l'XP reprend `bolt`).
    flower: Flower,
    award: Award,
    /** La poignée d'une ligne en cours de déplacement — les points de la bande gauche du legacy web. */
    "grip-vertical": GripVertical,
    /** Un arbre planté, quand le geste de la boutique n'a pas d'image. */
    seedling: Sprout,
    /** Un repas offert, quand le geste de la boutique n'a pas d'image. */
    utensils: Utensils,
    /**
     * Les trois points d'un menu (« Plus »), et la coupure d'un classement. Horizontaux partout (daily du 04/11 :
     * horizontaux chez le tuteur, verticaux chez l'élève) : la version verticale n'est plus au registre.
     */
    "ellipsis-h": Ellipsis,
    /** « Revoir le quiz » de prérequis, dans l'en-tête d'une activité (le glyphe d'aujourd'hui). */
    "clipboard-check": ClipboardCheck,
    /** « La correction t'a aidé ? » : oui, non. */
    "thumbs-up": ThumbsUp,
    "thumbs-down": ThumbsDown,

    // Le live de l'élève : sa barre de contrôle, ses vignettes, son mode, « Un problème ? ».
    "microphone-slash": MicOff,
    /** Quitter le live : le combiné barré, quand l'ancien code tournait le combiné à 225°. */
    "phone-slash": PhoneOff,
    /** « Un problème ? » — le stéthoscope d'aujourd'hui. */
    stethoscope: Stethoscope,
    /** Le partage d'écran du tuteur. */
    desktop: Monitor,
    /** Arrêter le partage d'écran : l'écran barré d'une croix, pendant le partage (Wajih, 05/11). */
    "desktop-slash": ScreenShareOff,

    // L'énoncé que l'élève lit : « Aa » ouvre « Ma lecture ». La flèche de retour attend l'échelle de dépannage (R53).
    /** « Aa », la lecture de l'énoncé — le glyphe de l'ancien bouton, dessiné. */
    font: ALargeSmall,
    "arrow-left": ArrowLeft,

    /** Vers la question d'accès du navigateur (`<PermissionPromptCoach>`), tournée vers son coin. */
    "arrow-up": ArrowUp,
} satisfies Record<string, LucideIcon>

/** Every glyph the DS can draw — a literal union, not `string`. */
export type IconName = keyof typeof ICONS
